import { Icon } from 'client/widget-components/basicComponents/Icon/Icon';
import React from 'react';
import styled, { css } from 'styled-components';
import { localizationService } from '@shared-services/localization-service';
import { Booking } from '../../types';
import CloseButton from '../CloseButton';
import { Modal, Title } from '../Modal';

const getString = (key: string) => {
    return localizationService.str(
        `ui.runtimessr.calbooking.confirmation.success.${key}`
    );
};
export enum BookingDataViewType {
    SUCCESS = 'success',
    CANCELATION = 'cancelation',
}
interface BookingDataViewProps {
    bookingData: Booking;
    title: string;
    children?: React.ReactNode;
    dataAuto?: string;
    onClose?: () => void;
    svgIcon?: React.ReactNode;
    type?: BookingDataViewType;
}

export const BookingDataView = ({
    bookingData,
    children,
    dataAuto,
    onClose,
    title,
    svgIcon,
    type = BookingDataViewType.SUCCESS,
}: BookingDataViewProps) => {
    const startDate = new Date(bookingData.startTime);
    const endDate = new Date(bookingData.endTime);

    const datePart = startDate.toLocaleDateString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });

    const timeOptions: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    };

    const startTime = startDate.toLocaleTimeString(undefined, timeOptions);
    const endTime = endDate.toLocaleTimeString(undefined, timeOptions);

    const timeZoneName = startDate
        .toLocaleTimeString(undefined, {
            timeZoneName: 'long',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        })
        .split(' ')
        .slice(2)
        .join(' ');

    return (
        <Modal data-auto={dataAuto}>
            <BookingDataViewWrapper>
                {onClose && <CloseButton onClick={onClose} />}
                <Header>
                    {svgIcon && (
                        <IconWrapper type={type}>{svgIcon}</IconWrapper>
                    )}
                    <Title>{title}</Title>
                </Header>
                <hr />
                <Details>
                    <Detail>
                        <Label>{getString('what')}:</Label>
                        <Value>{bookingData.title}</Value>
                    </Detail>
                    <Detail>
                        <Label>{getString('when')}:</Label>
                        <WhenContent
                            isCanceled={
                                type === BookingDataViewType.CANCELATION
                            }
                        >
                            <span>{datePart}</span>
                            <span>{`${startTime} - ${endTime} ${timeZoneName}`}</span>
                        </WhenContent>
                    </Detail>
                    <Detail>
                        <Label>{getString('who')}:</Label>
                        <Attendees>
                            <div>
                                <Value>
                                    {bookingData.user.name}
                                    <HostTag>{getString('host')}</HostTag>
                                </Value>
                                <Value>{bookingData.userPrimaryEmail}</Value>
                            </div>
                            {bookingData.attendees.map((attendee) => (
                                <div key={attendee.email}>
                                    <Value>{attendee.name}</Value>
                                    <Value>{attendee.email}</Value>
                                </div>
                            ))}
                        </Attendees>
                    </Detail>
                    <Detail>
                        <Label>{getString('where')}:</Label>
                        {bookingData.videoCallUrl ? (
                            <Value>
                                <a
                                    href={bookingData.videoCallUrl}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    {getString('videoCallLink')}
                                    <Icon
                                        name='icon-external-link'
                                        styles={{}}
                                    />
                                </a>
                            </Value>
                        ) : (
                            <Value>{bookingData.location}</Value>
                        )}
                    </Detail>
                </Details>
                {children && (
                    <>
                        <hr />
                        <Note>{children}</Note>
                    </>
                )}
            </BookingDataViewWrapper>
        </Modal>
    );
};

export const calcomColors = {
    bgSuccess: '#e8fbee',
    textSuccess: '#60ac68',
    hostTagBg: '#dfe7fb',
    hostTagText: '#2d3f81',
    bgCancelation: '#f5e3e2',
    textCancelation: '#ca3932',
};

const BookingDataViewWrapper = styled.div`
    margin: 0 auto;
    hr {
        height: 1px;
        background: #ccc;
        width: 100%;
        margin-block: 20px;
    }
    color: #555;
    font-weight: 500;
    font-size: 13px;
`;

const Details = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    line-height: 30px;
`;

const Detail = styled.div`
    display: grid;
    grid-template-columns: 50px 1fr;
    a {
        display: flex;
        align-items: center;
        gap: 4px;
        text-decoration: none;
    }
`;

const Label = styled.span`
    font-weight: 600;
    text-align: start;
`;

const Attendees = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    > div {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
    }
`;

const Value = styled.span`
    text-align: start;
    word-break: break-word;
`;

const WhenContent = styled.span<{ isCanceled: boolean }>`
    text-align: start;
    display: flex;
    flex-direction: column;
    ${({ isCanceled }) =>
        isCanceled &&
        css`
            text-decoration: line-through;
        `}
`;

const HostTag = styled.span`
    background-color: ${calcomColors.hostTagBg};
    color: ${calcomColors.hostTagText};
    padding: 2px 6px;
    box-sizing: border-box;
    border-radius: 4px;
    margin-inline-start: 8px;
`;

const Note = styled.div`
    display: flex;
    gap: 8px;
    justify-content: center;
    color: #555;
    width: 100%;
    span {
        text-decoration: underline;
        cursor: pointer;
        &[data-disabled='true'] {
            pointer-events: none;
            opacity: 0.5;
        }
    }
`;

const Header = styled.div`
    text-align: center;
    display: flex;
    flex-direction: column;
`;

const iconColors = {
    [BookingDataViewType.SUCCESS]: {
        bg: calcomColors.bgSuccess,
        text: calcomColors.textSuccess,
    },
    [BookingDataViewType.CANCELATION]: {
        bg: calcomColors.bgCancelation,
        text: calcomColors.textCancelation,
    },
};

const IconWrapper = styled.div<{ type: BookingDataViewType }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 58px;
    height: 58px;
    border-radius: 50%;
    background-color: ${({ type }) => iconColors[type]?.bg || 'transparent'};
    margin: 0 auto 16px auto;

    svg {
        width: 32px;
        height: 32px;
        stroke: ${({ type }) => iconColors[type]?.text || 'currentColor'};
        color: ${({ type }) => iconColors[type]?.text || 'currentColor'};
        stroke-width: 2;
        fill: none;
    }
`;
